import { createReducer, on } from '@ngrx/store';
import { GeneralError, SortDirection } from 'vidis-common';
import { Prescription, PrescriptionFilter, PrescriptionPagination, PrescriptionSort } from 'vidis-common/prescription';
import { PrescriptionsApiActions, PrescriptionsStoreActions } from '../actions/prescriptions.action';

export interface State {
  hasHiddenPrescriptions: boolean;
  items: Prescription[];
  errors: GeneralError[];
  pagination: PrescriptionPagination;
  totalItems: number;
  filter: PrescriptionFilter;
  loading: boolean;
  isLoaded: boolean;
}

const initialState: State = {
  hasHiddenPrescriptions: false,
  items: undefined,
  errors: undefined,
  pagination: {
    page: 0,
    pageSize: 10,
    totalPages: undefined,
    sort: PrescriptionSort.validityDate,
    order: SortDirection.ascending,
  },
  totalItems: undefined,
  filter: undefined,
  loading: undefined,
  isLoaded: false
};

export const prescriptionsReducer = createReducer(
  initialState,
  on(PrescriptionsApiActions.fetchPrescriptionsByPage, PrescriptionsApiActions.fetchCount, (state): State => ({
    ...state,
    loading: true
  })),
  on(PrescriptionsStoreActions.setPrescriptions, (state, {prescriptionResult}): State => ({
    ...state,
    hasHiddenPrescriptions: prescriptionResult?.hasHiddenPrescriptions || initialState.hasHiddenPrescriptions,
    items: prescriptionResult?.items || initialState.items,
    totalItems: prescriptionResult?.totalItems,
    errors: prescriptionResult?.errors || initialState.errors,
    pagination: {
      ...state.pagination,
      page: prescriptionResult?.page || initialState.pagination.page,
      totalPages: prescriptionResult?.totalPages || initialState.pagination.totalPages,

    },
    loading: false,
    isLoaded: true
  })),
  on(PrescriptionsStoreActions.setPrescriptionsCount, (state, {countPrescriptionResult}): State => ({
    ...state,
    totalItems: countPrescriptionResult?.count !== undefined ? countPrescriptionResult.count : initialState.totalItems,
    loading: false
  })),
  on(PrescriptionsApiActions.fetchSinglePrescription, (state): State => ({
    ...state,
    loading: true
  })),
  on(PrescriptionsStoreActions.setSinglePrescription, (state, {prescription}): State => ({
    ...state,
    items: state.items ? state.items.map(p => p.ridCode === prescription.ridCode
      ? {
        ...prescription
      }
      : p
    ) : [prescription],
    loading: false
  })),
  on(PrescriptionsStoreActions.updatePrescription, (state, {
    ridCode,
    reservationPharmacy,
    visibilityPharmacy,
    visibilityPrescriber
  }): State => ({
    ...state,
    items: state.items.map((prescription): Prescription =>
      prescription.ridCode === ridCode
        ? {
          ...prescription,
          reservation: reservationPharmacy ? { pharmacy: reservationPharmacy } : prescription.reservation,
          visibilityPrescriber: visibilityPrescriber || prescription.visibilityPrescriber,
          visibilityPharmacy: visibilityPharmacy === null ? undefined : visibilityPharmacy || prescription.visibilityPharmacy
        }
        : prescription
    )
  })),
  on(PrescriptionsStoreActions.updatePagination, (state, { pagination }): State => ({
    ...state,
    pagination: {
      ...state.pagination,
      page: pagination?.page !== undefined ? pagination.page : state.pagination.page,
      pageSize: pagination?.pageSize || state.pagination.pageSize,
      order: pagination?.order || state.pagination.order,
    }
  })),
  on(PrescriptionsStoreActions.updateFiltering, (state, {filters}): State => ({
    ...state,
    filter: filters
      ? {
        creationDateRange: filters.creationDateRange.from && filters.creationDateRange.until
          ? filters.creationDateRange
          : state.filter?.creationDateRange,
        expirationDateRange: filters.expirationDateRange.from && filters.expirationDateRange.until
          ? filters.expirationDateRange
          : state.filter?.expirationDateRange
      }
      : undefined
  })),
  on(PrescriptionsStoreActions.removeOneFilter, (state, {key}): State => {
    switch (key) {
    case 'expirationDateRange':
      return {
        ...state,
        filter: state.filter?.creationDateRange
          ? {
            ...state.filter,
            expirationDateRange: undefined
          }
          : undefined
      };
    case 'creationDateRange':
      return {
        ...state,
        filter: state.filter?.expirationDateRange
          ? {
            ...state.filter,
            creationDateRange: undefined
          }
          : undefined
      };
    default:
      return state;
    }
  }),
  on(PrescriptionsStoreActions.removeReservation, (state, {ridCode}): State => ({
    ...state,
    items: state.items.map(p => p.ridCode === ridCode
      ? {
        ...p,
        reservation: undefined
      }
      : p
    )
  })),
  on(PrescriptionsStoreActions.clearPrescriptions, (): State => (initialState)),
);
